import Phaser from 'phaser';
import type { Direction } from '@snake-royalle/shared';

const OPPOSITE: Record<Direction, Direction> = {
  UP: 'DOWN', DOWN: 'UP', LEFT: 'RIGHT', RIGHT: 'LEFT',
};

// Snake moves once every 200ms server-side. The first input is sent
// immediately; only follow-up buffered turns are spaced to this cadence.
const DISPATCH_INTERVAL_MS = 200;
// Keep only one follow-up turn buffered. This preserves quick cornering
// without making controls feel like they are playing old inputs later.
const MAX_QUEUED = 1;

/**
 * Unified input manager — keyboard + touch.
 *
 * Direction queue:
 *  - Keys are read as discrete `keydown` events (not polled), so quick taps
 *    register the moment they happen.
 *  - The first valid turn is sent immediately to avoid adding client-side
 *    delay on top of network latency.
 *  - One follow-up direction can be buffered and dispatched on the next
 *    server-move cadence, matching the expected cornering behavior.
 *  - Reverse moves (UP→DOWN etc.) are silently dropped — they're invalid
 *    server-side anyway.
 *
 * Net effect: pressing DOWN-then-RIGHT in 50ms now produces a DOWN turn
 * immediately and a RIGHT turn for the next move, instead of adding a full
 * 200ms delay before the first turn.
 */
export class InputManager {
  private scene: Phaser.Scene;
  private onDirectionChange: (dir: Direction) => void;
  private isMobile: boolean;

  private lastDispatched: Direction = 'RIGHT';
  private queue: Direction[] = [];
  private nextDispatchAt: number = 0;

  constructor(scene: Phaser.Scene, onDirectionChange: (dir: Direction) => void) {
    this.scene = scene;
    this.onDirectionChange = onDirectionChange;
    this.isMobile = scene.registry.get('isMobile') ?? false;

    this.setupKeyboard();
    if (this.isMobile) this.setupTouch();
  }

  private setupKeyboard(): void {
    if (!this.scene.input.keyboard) return;

    // Edge-triggered keydown listeners — capture every distinct press
    const press = (dir: Direction) => () => this.enqueue(dir);

    this.scene.input.keyboard.on('keydown-UP',    press('UP'));
    this.scene.input.keyboard.on('keydown-DOWN',  press('DOWN'));
    this.scene.input.keyboard.on('keydown-LEFT',  press('LEFT'));
    this.scene.input.keyboard.on('keydown-RIGHT', press('RIGHT'));
    this.scene.input.keyboard.on('keydown-W',     press('UP'));
    this.scene.input.keyboard.on('keydown-S',     press('DOWN'));
    this.scene.input.keyboard.on('keydown-A',     press('LEFT'));
    this.scene.input.keyboard.on('keydown-D',     press('RIGHT'));
  }

  private setupTouch(): void {
    let startX = 0;
    let startY = 0;

    this.scene.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      startX = pointer.x;
      startY = pointer.y;
    });

    this.scene.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const dx = pointer.x - startX;
      const dy = pointer.y - startY;
      const minSwipe = 30;
      if (Math.abs(dx) < minSwipe && Math.abs(dy) < minSwipe) return;

      if (Math.abs(dx) > Math.abs(dy)) {
        this.enqueue(dx > 0 ? 'RIGHT' : 'LEFT');
      } else {
        this.enqueue(dy > 0 ? 'DOWN' : 'UP');
      }
    });
  }

  /** Push a direction onto the queue if it's a meaningful turn. */
  private enqueue(dir: Direction): void {
    // Reject reverse from the most-recent decided direction
    const last = this.queue.length > 0 ? this.queue[this.queue.length - 1] : this.lastDispatched;
    if (dir === last) return;
    if (OPPOSITE[last] === dir) return;

    const now = Date.now();
    if (now >= this.nextDispatchAt && this.queue.length === 0) {
      this.dispatch(dir, now);
      return;
    }

    if (this.queue.length >= MAX_QUEUED) return;
    this.queue.push(dir);
  }

  /** Called every frame from GameScene.update. Drains the queue at server tick rate. */
  update(): void {
    if (this.queue.length === 0) return;
    const now = Date.now();
    if (now < this.nextDispatchAt) return;

    const dir = this.queue.shift()!;
    this.dispatch(dir, now);
  }

  getLastDirection(): Direction {
    return this.lastDispatched;
  }

  /** Sync the locally-tracked direction to whatever the server says the
   *  snake is actually doing. Without this the queue rejects valid turns
   *  at match start (the client's stale 'RIGHT' default treats a press
   *  matching the snake's real direction as a no-op, and the OPPOSITE
   *  direction as an illegal reverse). Call on first state arrival and
   *  after every respawn. */
  syncDirection(dir: Direction): void {
    this.lastDispatched = dir;
    this.queue = [];
    this.nextDispatchAt = 0;
  }

  private dispatch(dir: Direction, now: number): void {
    this.lastDispatched = dir;
    this.onDirectionChange(dir);
    this.nextDispatchAt = now + DISPATCH_INTERVAL_MS;
  }

  destroy(): void {
    this.scene.input.removeAllListeners();
    if (this.scene.input.keyboard) {
      this.scene.input.keyboard.removeAllListeners();
    }
    this.queue = [];
  }
}
